import React, { useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import SortableList, { SortableItem } from "react-easy-sort";
import arrayMove from "array-move";
import { v4 as uuidv4 } from "uuid";
import ImageCropper from "./ImageCropper";
import {
  Box,
  Text,
  Skeleton,
  useColorModeValue,
  IconButton,
  VStack,
} from "@chakra-ui/react";
import { FaCirclePlus } from "react-icons/fa6";
import { FaTrashAlt } from "react-icons/fa";
import { GetUserImages, RemoveImage, UploadImages } from "../services/backend";
import { resolveImageUrl } from "../util/profileImage";
import { useToast } from "@chakra-ui/react";

const tileProps = {
  w: "100%",
  aspectRatio: "1",
  maxW: { lg: "168px" },
};

const ImageUploader = ({ onImageUpload, sendImagesData, minPhotos = 2 }) => {
  const toast = useToast();
  const [images, setImages] = useState([]);
  const [croppingImage, setCroppingImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const uploadInFlightRef = useRef(false);
  let user = null;
  try {
    const raw = localStorage.getItem("user");
    user = raw ? JSON.parse(raw) : null;
  } catch {
    user = null;
  }
  const userId = user?.id;

  const muted = useColorModeValue("gray.500", "whiteAlpha.550");
  const hint = useColorModeValue("gray.600", "whiteAlpha.600");
  const dropBorder = useColorModeValue("gray.300", "whiteAlpha.200");
  const dropBorderHover = useColorModeValue("brand.400", "brand.400");
  const dropBg = useColorModeValue("gray.50", "rgba(255,255,255,0.04)");
  const dropBgHover = useColorModeValue("brand.50", "rgba(52,211,153,0.08)");
  const tileRing = useColorModeValue("rgba(15,23,42,0.08)", "rgba(255,255,255,0.1)");
  const tileShadow = useColorModeValue(
    "0 4px 14px rgba(15,23,42,0.08)",
    "0 8px 28px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)"
  );
  const tileShadowHover = useColorModeValue(
    "0 8px 24px rgba(15,23,42,0.12)",
    "0 12px 36px rgba(52,211,153,0.12), 0 0 0 1px rgba(110,231,183,0.25)"
  );
  const skStart = useColorModeValue("gray.100", "whiteAlpha.100");
  const skEnd = useColorModeValue("gray.200", "whiteAlpha.200");
  const plusColor = useColorModeValue("#64748b", "#94a3b8");
  const dropFocusRing = useColorModeValue("0 0 0 3px rgba(52,211,153,0.15)", "0 0 0 3px rgba(52,211,153,0.2)");

  const fetchImages = async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await GetUserImages(userId);
      const fetchedImages = response.map((img) => ({
        id: uuidv4(),
        dbId: img.id,
        file: img.fileName,
        url: resolveImageUrl(img),
      }));
      setImages(fetchedImages);
      sendImagesData?.(fetchedImages);
    } catch (error) {
      console.error("Failed to fetch images:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once when userId is available
  }, [userId]);

  const onDrop = (acceptedFiles) => {
    if (uploadInFlightRef.current || isUploading) return;
    const file = acceptedFiles[0];
    if (!file) return;
    const fileUrl = URL.createObjectURL(file);
    setCroppingImage(fileUrl);
  };

  const addCroppedImage = async (blob, fileUrl) => {
    if (uploadInFlightRef.current) return;
    uploadInFlightRef.current = true;
    setIsUploading(true);
    // Close crop modal immediately so Save can't be clicked twice.
    setCroppingImage(null);

    const newImage = {
      id: uuidv4(),
      file: blob,
      url: fileUrl,
    };

    try {
      onImageUpload?.(newImage);
      setImages((prev) => [...prev, newImage]);

      const data = new FormData();
      data.append("UserId", userId);
      data.append("imageCollection", newImage.file);
      await UploadImages(data);
      toast({
        description: "Your image has been uploaded successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
      fetchImages();
    } catch (error) {
      console.error("Error uploading image:", error);
      setImages((prev) => prev.filter((img) => img.id !== newImage.id));
      toast({
        description: "Could not upload image. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
    } finally {
      uploadInFlightRef.current = false;
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
    disabled: isUploading,
  });

  const onSortEnd = (oldIndex, newIndex) => {
    setImages(arrayMove(images, oldIndex, newIndex));
  };

  const RemovePhoto = (img) => {
    setImages((prevImages) => {
      const next = prevImages.filter((propimage) => propimage.id !== img.id);
      sendImagesData?.(next);
      return next;
    });
    try {
      RemoveImage(img.dbId);
      toast({
        description: "Image has been removed from your profile.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
    } catch (error) {
      console.error("Error removing image:", error);
    }
  };

  const renderSkeletons = () =>
    Array(Math.max(minPhotos, 2))
      .fill(0)
      .map((_, index) => (
        <Skeleton
          key={index}
          {...tileProps}
          borderRadius="xl"
          startColor={skStart}
          endColor={skEnd}
        />
      ));

  const needsMore = images.length < minPhotos;
  const addTileProps = needsMore
    ? {
        ...tileProps,
        borderColor: dropBorderHover,
        bg: dropBgHover,
        boxShadow: dropFocusRing,
      }
    : { ...tileProps, borderColor: dropBorder, bg: dropBg };

  return (
    <VStack align="stretch" spacing={4} w="100%" py={1}>
      <Text fontSize="xs" color={hint} lineHeight="tall">
        {needsMore
          ? `Add ${minPhotos - images.length} more photo${minPhotos - images.length === 1 ? "" : "s"} to continue (${images.length}/${minPhotos}).`
          : "Drag tiles to reorder—your first photo is shown first on your profile."}
      </Text>

      <Box overflowX="auto" w="100%" sx={{ WebkitOverflowScrolling: "touch" }}>
        {isLoading ? (
          <Box className="payinapo-image-sortable" aria-busy="true">
            {renderSkeletons()}
          </Box>
        ) : (
          <SortableList
            as="div"
            onSortEnd={onSortEnd}
            className="payinapo-image-sortable"
            draggedItemClassName="payinapo-sortable-dragging"
          >
            {images.map((img) => (
              <SortableItem key={img.id}>
                <Box
                  position="relative"
                  {...tileProps}
                  borderRadius="xl"
                  overflow="hidden"
                  boxShadow={tileShadow}
                  borderWidth="1px"
                  borderColor={tileRing}
                  transition="transform 0.2s ease, box-shadow 0.2s ease"
                  _hover={{
                    transform: "translateY(-3px)",
                    boxShadow: tileShadowHover,
                  }}
                >
                  <Box
                    as="img"
                    src={img.url}
                    alt=""
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    display="block"
                    draggable={false}
                  />
                  <IconButton
                    type="button"
                    aria-label="Remove photo"
                    icon={<FaTrashAlt />}
                    size="sm"
                    position="absolute"
                    bottom={2}
                    right={2}
                    borderRadius="full"
                    variant="solid"
                    colorScheme="red"
                    boxShadow="md"
                    opacity={0.92}
                    _hover={{ opacity: 1, transform: "scale(1.05)" }}
                    onClick={() => RemovePhoto(img)}
                  />
                </Box>
              </SortableItem>
            ))}
            {images.length < 8 && (
              <Box
                {...getRootProps()}
                {...addTileProps}
                borderWidth="2px"
                borderStyle="dashed"
                borderRadius="xl"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                cursor={isUploading ? "not-allowed" : "pointer"}
                opacity={isUploading ? 0.55 : 1}
                pointerEvents={isUploading ? "none" : "auto"}
                transition="all 0.2s ease"
                _hover={
                  isUploading
                    ? undefined
                    : {
                        borderColor: dropBorderHover,
                        bg: dropBgHover,
                        boxShadow: dropFocusRing,
                      }
                }
              >
                <input {...getInputProps()} disabled={isUploading} />
                <FaCirclePlus size={needsMore ? 34 : 30} color={plusColor} />
                <Text mt={2} fontSize="sm" fontWeight={needsMore ? "semibold" : "medium"} color={muted}>
                  {isUploading ? "Uploading…" : "Add photo"}
                </Text>
              </Box>
            )}
          </SortableList>
        )}
      </Box>

      {croppingImage && (
        <ImageCropper imageSrc={croppingImage} onCropDone={addCroppedImage} onCancel={() => setCroppingImage(null)} />
      )}
    </VStack>
  );
};

export default ImageUploader;
